<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import pick from 'lodash/pick'
import get from 'lodash/get'
import RetailerRemoveModal from './modals/retailer-remove'
import ValidationMixin from '@lib/mixins/validation'

export default {
  name: 'RetailerEdit',
  components: {
    RetailerRemoveModal,
  },
  mixins: [ValidationMixin],
  props: {
    retailer: {
      type: Object,
      default: null,
    },
  },
  data() {
    const validatorURL = (rule, value, callback) => {
      if (value.length && /^(https?:\/\/)/.test(value) !== true) {
        callback(new Error('Please include a http:// or https:// prefix on the URL entered'))
      } else {
        callback()
      }
    }

    return {
      loading: false,
      progress: false,
      categoriesList: [],
      form: {
        active: false,
        name: '',
        description: '',
        category: '',
        website: 'https://',
        logo: '',
        providerId: null,
      },
      rules: {
        name: [
          {
            required: true,
            message: 'Retailer name is required',
          },
        ],
        description: [
          {
            required: true,
            message: 'Retailer description is required',
          },
        ],
        category: {
          required: true,
          message: 'Category is required',
        },
        website: [
          {
            validator: validatorURL,
            trigger: 'change',
          },
        ],
        providerId: {
          required: true,
          message: 'Category is required',
        },
      },
      modals: {
        remove: false,
      },
      minLogoDimensions: [500, 500],
    }
  },
  computed: {
    ...mapState('me', [
      'provider',
    ]),
    ...mapState('providers', {
      getProvider: 'provider',
      providers: 'providers',
    }),
    ...mapGetters('me', [
      'canDeleteRetailer',
      'canUpdateRetailer',
    ]),
    status() {
      return this.form.active ? 'Active' : 'Inactive'
    },
    isEdit() {
      return !!this.retailer
    },
    isFormChanged() {
      if (!this.isEdit) {
        return true
      }

      const keys = Object.keys(this.form)
      for (const key of keys) {
        if (this.retailer[key] !== this.form[key]) {
          return true
        }
      }
      return false
    },
    logoLabel() {
      const label = ['Retailer Logo']
      const { minLogoDimensions } = this
      if (minLogoDimensions && minLogoDimensions.length >= 2) {
        label.push(`(${minLogoDimensions[0]} x ${minLogoDimensions[1]} px minimum)`)
      }

      return label.join(' ')
    },
    providerName() {
      const provider = this.getProvider(this.form.providerId)

      return get(provider, 'name', '')
    },
    filteredCategories() {
      return this.categoriesList.filter(c => c.providerId === this.form.providerId)
    },
  },
  watch: {
    'form.providerId'() {
      this.form.category = ''
      this.$nextTick(() => {
        this.$refs.form.fields.find(f => f.prop === 'category').clearValidate()
      })
    },
  },
  async created() {
    if (this.isEdit) {
      this.form = {
        ...this.form,
        ...pick(this.retailer, [
          'id',
          'active',
          'name',
          'description',
          'website',
          'logo',
          'providerId',
        ]),
      }

      if (!this.form.website) {
        this.form.website = 'https://'
      }
    } else {
      this.form.providerId = this.provider.id
    }

    this.loading = true

    await this.fetchCategories()

    this.loading = false
  },
  methods: {
    ...mapActions('retailer', [
      'createRetailer',
      'updateRetailer',
    ]),
    ...mapActions('category', [
      'getCategories',
    ]),
    async onSave() {
      this.clearBackendErrors()
      if (!this.isFormChanged) return

      const formValid = await this.$refs.form.validate().catch(() => false)
      if (!formValid) return

      this.progress = true

      const options = { headers: { 'x-provider-id': this.form.providerId } }
      const response = await this[this.isEdit ? 'updateRetailer' : 'createRetailer']({
        form: this.transformFormData(this.form),
        options,
      })
      const [error] = response

      if (error) {
        this.processBackendErrors(error)
      }

      this.$notifier({
        response,
        errorTitle: `Unable to save ${this.isEdit ? 'changes' : 'retailer'}`,
        errorMsg: 'Server error',
        successTitle: 'Retailer saved',
        successMsg: `${this.isEdit ? 'Changes' : 'New retailer'} successfully saved.`,
      }).then(success => {
        if (success) {
          const route = {
            name: 'retailer-details',
            params: { id: success.id },
          }

          if (this.isEdit) {
            this.$router.push(route)
            this.$emit('update')
          } else {
            this.$router.replace(route)
          }
        }
      })

      this.progress = false
    },
    transformFormData(data) {
      return {
        ...data,
        category: [`/categories/${data.category}`],
        website: (data.website === 'https://' || data.website === 'http://') ? '' : data.website,
      }
    },
    async fetchCategories(newlyAdded) {
      const [, response] = await this.getCategories({
        params: {
          perPage: 100,
        },
      })

      if (response && response.items) {
        this.categoriesList = response.items

        if (newlyAdded) {
          this.form.category = newlyAdded.id
          return
        }

        if (this.isEdit) {
          const firstFormCatName = get(this.retailer, 'category.name', '')

          if (firstFormCatName) {
            const foundCategory = this.categoriesList.find(c => c.name === firstFormCatName)

            if (foundCategory) {
              this.form.category = foundCategory.id
            }
          }
        }
      }
    },
    closeModal() {
      this.modals.remove = false

      this.$router.push({
        name: 'retailers',
      })
    },
    selectCategory(value) {
      if (value === 'new') {
        this.$modal.show('category-create', {
          on: {
            added: this.fetchCategories,
          },
        })
        return
      }
      this.form.category = value
    },
  },
}
</script>

<template>
  <data-box :status="false">
    <base-loader
      v-if="loading"
      theme="state"
      center
    />
    <el-form
      v-else
      ref="form"
      :model="form"
      :rules="rules"
      label-position="top"
      class="card-form card-form--wide"
    >
      <el-form-item
        label="Status"
        prop="active"
      >
        <el-switch
          v-model="form.active"
          active-color="#13ce66"
          inactive-color="#a7a7a7"
          :active-value="true"
          :inactive-value="false"
        />
        <span :class="$style.status">
          {{ status }}
        </span>
      </el-form-item>
      <el-form-item
        v-if="provider.id"
        label="Provider Name"
      >
        <el-input
          disabled
          :value="providerName"
        />
      </el-form-item>
      <el-form-item
        v-else
        label="Provider Name"
        prop="providerId"
        :error="backendErrors.providerId"
      >
        <el-select
          v-model="form.providerId"
          :disabled="!!retailer"
          placeholder="Select provider"
        >
          <el-option
            v-for="provider in providers"
            :key="provider.id"
            :label="provider.name"
            :value="provider.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item
        label="Retailer Name"
        prop="name"
        :error="backendErrors.name"
      >
        <el-input v-model="form.name" />
      </el-form-item>
      <el-form-item
        label="Retailer Description"
        prop="description"
        :error="backendErrors.description"
      >
        <the-textarea
          v-model="form.description"
          :max-chars="false"
        />
      </el-form-item>
      <el-form-item
        label="Category"
        prop="category"
        :error="backendErrors.category"
      >
        <el-select
          :value="form.category"
          :placeholder="form.providerId ? 'Select category' : 'Select provider first'"
          :disabled="!form.providerId"
          @input="selectCategory"
        >
          <el-option
            value="new"
            label="Create New Category"
          />

          <el-option
            v-for="category in filteredCategories"
            :key="category.id"
            :label="category.name"
            :value="category.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item
        label="Retailer Website"
        prop="website"
        :error="backendErrors.website"
      >
        <el-input v-model="form.website" />
      </el-form-item>
      <el-form-item
        :label="logoLabel"
        prop="logo"
      >
        <hub-image-uploader
          v-model="form.logo"
          crop="1:1"
          :dimensions-min="minLogoDimensions"
        />
      </el-form-item>
      <retailer-remove-modal
        v-if="isEdit && modals.remove"
        :id="retailer.id"
        :visible.sync="modals.remove"
        @close-modal="closeModal"
      />

      <div class="card-form__footer">
        <el-button
          v-if="isEdit && canDeleteRetailer"
          type="danger"
          :disabled="progress"
          @click="modals.remove = true"
        >
          Delete Retailer
        </el-button>
        <div>
          <el-button
            plain
            :disabled="progress"
            @click="$emit('cancel')"
          >
            Cancel
          </el-button>
          <el-button
            v-if="!isEdit || canUpdateRetailer"
            type="primary"
            :disabled="!isFormChanged || progress"
            @click="onSave"
          >
            Save Retailer
          </el-button>
        </div>
      </div>
    </el-form>
  </data-box>
</template>

<style lang="scss" module>
.status {
  margin-left: rem(14px);
}
</style>
